<template>
	<div class="row">
		<div class="col">
			<div class="form-label">Funds</div>
			<VueMultiselect
					v-model="internalFundsSelected"
					:disabled="isScopedToInvestment()"
					:multiple="multiSelect"
					:placeholder="compFundPlaceholder"
					label="name"
					:close-on-select="!multiSelect"
					track-by="name"
					v-on:select="onFundsChanged"
					v-on:remove="onFundsChanged"
					:options="funds">
			</VueMultiselect>
		</div>
		<div class="col">
			<div class="form-label">Investments</div>
			<VueMultiselect
					v-model="internalInvestmentsSelected"
					:disabled="isScopedToInvestment()"
					:multiple="multiSelect"
					:placeholder="compInvestPlaceholder"
					label="name"
					:close-on-select="!multiSelect"
					track-by="name"
					v-on:select="onInvestmentsChanged"
					v-on:remove="onInvestmentsChanged"
					:options="availableInvestments">
			</VueMultiselect>
		</div>

		<div v-if="includeDateRange" class="col">
			<div class="form-label">* Period</div>
			<VueMultiselect
				v-model="selectedPeriod"
				:multiple="false"
				:close-on-select="true"
				:placeholder="'Select Period'"
				:options="periodOptions">
			</VueMultiselect>
		</div>
	</div>
	<div v-if="includeDateRange" class="row mt-2">
		<div class="col">
			<div class="form-label">Year</div>
			<VueMultiselect
				v-model="selectedYears"
				:multiple="true"
				:close-on-select="false"
				:placeholder="compYearPlaceholder"
				:disabled="selectedPeriod === 'LTM' || selectedPeriod === 'YTD'"
				:options="yearOptions">
			</VueMultiselect>
		</div>
		<div class="col">
			<div class="form-label">Quarter</div>
			<VueMultiselect
				v-model="selectedQuarters"
				:multiple="true"
				:close-on-select="false"
				:placeholder="compQuarterPlaceholder"
				:disabled="selectedPeriod !== 'Quarterly'"
				:options="quarterOptions">
			</VueMultiselect>
		</div>
		<div class="col">
			<div class="form-label">Month</div>
			<VueMultiselect
				v-model="selectedMonths"
				:multiple="true"
				:close-on-select="false"
				:placeholder="compMonthPlaceholder"
				:disabled="selectedPeriod !== 'Monthly'"
				:options="monthOptions">
			</VueMultiselect>
		</div>
	</div>
</template>

<script type="text/javascript">

import FundDataService from "@/services/FundDataService";
import {InvestmentsApi} from "@/api/investment_api";
import { CustomExportApi } from "@/api/custom_export_api";
import VueMultiselect from 'vue-multiselect'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Collapses [0,1,2,6,7,8] into "Jan-Mar, Jul-Sep". Ranges read better than six
// month names in a disabled placeholder.
function summariseMonths(indices) {
	if (!indices.length) return ''
	const sorted = [...new Set(indices)].sort((a, b) => a - b)
	const runs = []
	let start = sorted[0], prev = sorted[0]
	for (const i of sorted.slice(1)) {
		if (i === prev + 1) { prev = i; continue }
		runs.push([start, prev]); start = i; prev = i
	}
	runs.push([start, prev])
	return runs
		.map(([a, b]) => (a === b ? MONTHS[a] : `${MONTHS[a]}-${MONTHS[b]}`))
		.join(', ')
}

export default {
	name: 'FundInvestmentsSelector',
	inheritAttrs: false,

	mixins: [],

	components: {
		VueMultiselect
	},

	filters: {
	},

	provide: function() {
		return {
		}
	},

	inject: [],

	emits: ['update:fundsSelected', 'update:investmentsSelected', 'investmentsChanged', 'update:periodSelected', 'update:yearsSelected', 'update:quartersSelected', 'update:monthsSelected',
			// Counts of what is on offer, so the caller can distinguish "everything
			// selected" from "nothing selected" and omit the parameter in both
			// cases — 42 investment GUIDs is ~1.6KB of query string that means
			// exactly what an absent parameter already means.
			'update:fundsAvailable', 'update:investmentsAvailable'],

	props: {
		fundsPlaceholder: {
			type: String,
			default() {
				return 'All Funds'
			}
		},

		investmentsPlaceholder: {
			type: String,
			default() {
				return 'All Investments'
			}
		},

		fundsSelected: {
			type: Array,
			default() {
				return []
			}
		},
		investmentsSelected: {
			type: Array,
			default() {
				return []
			}
		},
		investmentId: {
			type: String,
			default() {
				return null
			}
		},
		readOnly: {
			type: Boolean,
			default(){
				return false
			}
		},
		multiSelect: {
			type: Boolean,
			default() {
				return true
			}
		},
		includeDateRange: {
			type: Boolean,
			default() {
				return false
			}
		},
	},

	data() {
		return {
			funds: [],
			investments: [],
			availableInvestments: [],
			internalFundsSelected: [],
			internalInvestmentsSelected: [],
			selectedPeriod: null,
			selectedYears: [],
			selectedQuarters: [],
			selectedMonths: [],
			periodOptions: ['YTD', 'LTM', 'Annual', 'Quarterly', 'Monthly'],
			monthOptions: MONTHS.slice(),
			quarterOptions: ['Q1', 'Q2', 'Q3', 'Q4'],
			minYear: new Date().getFullYear()
		}
	},

	computed: {
		compFundPlaceholder() {
			let retPlaceholder = (this.internalFundsSelected && this.internalFundsSelected.length > 0) ? '' : this.fundsPlaceholder
			return retPlaceholder
		},
		compInvestPlaceholder() {
			let retPlaceholder = (this.internalInvestmentsSelected && this.internalInvestmentsSelected.length > 0) ? '' : this.investmentsPlaceholder
			return retPlaceholder
		},
		compYearPlaceholder() {
			if (this.selectedPeriod === 'LTM') {
				const today = new Date()
				const isDecThirtyFirst = today.getMonth() === 11 && today.getDate() === 31
				const currentYear = today.getFullYear()
				return isDecThirtyFirst
					? String(currentYear)
					: `${currentYear - 1}, ${currentYear}`
			}
			// YTD always resolves to the current year, and the control is locked,
			// so state the year rather than implying every year is in scope.
			if (this.selectedPeriod === 'YTD') {
				return String(new Date().getFullYear())
			}
			return this.selectedYears.length > 0 ? '' : 'All Years'
		},
		// Quarters are only a filter under Quarterly. For every other period this
		// control is inert, so rather than claim "All Quarters" it names whatever
		// actually governs the window.
		//
		// Quarters are derived only where the mapping is exact: a full year is
		// always Q1-Q4, but a month selection does not imply whole quarters (Jan
		// and Feb are not "Q1"), and YTD/LTM windows can end mid-quarter — so
		// those cases say what drives the selection instead of overstating it.
		compQuarterPlaceholder() {
			const p = this.selectedPeriod
			if (p === 'Quarterly') {
				return this.selectedQuarters.length > 0 ? '' : 'All Quarters'
			}
			if (p === 'Monthly') return 'Set by month'
			if (p === 'Annual') return 'Q1-Q4'
			// YTD and LTM can end mid-quarter, so no quarter figure is honest here.
			return '—'
		},

		// When the Month control is disabled the period already determines which
		// months are in scope, so show those rather than "All Months" or a blank —
		// same approach as compYearPlaceholder does for LTM.
		compMonthPlaceholder() {
			const p = this.selectedPeriod
			if (p === 'Monthly') {
				return this.selectedMonths.length > 0 ? '' : 'All Months'
			}
			if (p === 'Quarterly') {
				if (!this.selectedQuarters.length) return 'All Months'
				const idx = this.selectedQuarters.flatMap(q => {
					const first = (parseInt(q.slice(1), 10) - 1) * 3
					return [first, first + 1, first + 2]
				})
				return summariseMonths(idx)
			}
			if (p === 'YTD') {
				return summariseMonths([...Array(new Date().getMonth() + 1).keys()])
			}
			if (p === 'LTM') {
				const now = new Date()
				const start = new Date(now.getFullYear(), now.getMonth() - 11, 1)
				return `${MONTHS[start.getMonth()]} ${start.getFullYear()} - ${MONTHS[now.getMonth()]} ${now.getFullYear()}`
			}
			// Annual covers whole years
			return 'Jan-Dec'
		},

		yearOptions() {
			const currentYear = new Date().getFullYear()
			const years = []
			for (let y = currentYear; y >= this.minYear; y--) {
				years.push(String(y))
			}
			return years
		}
	},

	watch: {
		selectedPeriod(val) {
			if (val !== 'Quarterly') {
				this.selectedQuarters = []
			}
			if (val !== 'Monthly') {
				this.selectedMonths = []
			}
			// Years are ignored by the resolver for both of these, so clearing
			// avoids a selection that silently has no effect.
			if (val === 'LTM' || val === 'YTD') {
				this.selectedYears = []
			}
			this.$emit('update:periodSelected', val)
		},
		selectedYears(val) {
			this.$emit('update:yearsSelected', val)
		},
		selectedQuarters(val) {
			this.$emit('update:quartersSelected', val)
		},
		selectedMonths(val) {
			this.$emit('update:monthsSelected', val)
		}
	},

	created() {
	},

	mounted() {
		this.loadFunds()
		this.loadInvestments()
		this.loadMeta()
	},

	methods: {
		async loadMeta() {
			try {
				const meta = await CustomExportApi.getMeta()
				if (meta?.minYear) {
					this.minYear = meta.minYear
					CustomExportApi.setDataStartYear(meta.minYear)
				}
			} catch (err) {
				console.error('Failed to load export meta:', err)
			}
		},

		isScopedToInvestment() {
			return this.investmentId != null && this.readOnly
		},

		getAllInvestments() {
			return this.availableInvestments
		},

		loadFunds() {
			FundDataService.loadFunds().then(response => {
				this.funds = response.map(fund => ({
					id: fund.guid,
					name: fund.name
				}))
				this.$emit('update:fundsAvailable', this.funds.length)
			})
		},
		loadInvestments() {
			InvestmentsApi.getInvestments({ '$top': 1000 }).then(response => {
				this.investments = response.value || []
				this.generateInvestments(this.investmentId)
			})
		},

		generateInvestments(primaryInvestmentId) {
			// Need to regenerate the list of investments
			let funds = this.internalFundsSelected
			if (!this.internalFundsSelected) {
				funds = []
			} else if(this.internalFundsSelected.constructor && this.internalFundsSelected.constructor.name != 'Array') {
				funds = [this.internalFundsSelected]
			}

			const fundIds = funds.map(fund => (fund.id))
			let primaryInvestment = null
			this.availableInvestments = this.investments.filter(company => {
				if(fundIds.length == 0 && company.guid != primaryInvestmentId) {
					return true
				}
				if(company.guid == primaryInvestmentId) {
					primaryInvestment = company
					return true
				} 
				const companyFundIds = company.fundIds
				const intersectFunds = fundIds.filter(fundId => companyFundIds.includes(fundId))
				return intersectFunds.length > 0
			}).sort((c1, c2) => c1.name.localeCompare(c2.name))
			// Narrows with the fund filter, so "all" means all currently offered.
			this.$emit('update:investmentsAvailable', this.availableInvestments.length)

			this.internalInvestmentsSelected = primaryInvestment != null ? [primaryInvestment] : []
			this.$emit('update:fundsSelected', funds)
			this.$emit('update:investmentsSelected', this.internalInvestmentsSelected)
		},

		onFundsChanged() {
			this.internalInvestmentsSelected = []
			this.onInvestmentsChanged()
			this.generateInvestments(null)
		},

		onInvestmentsChanged() {
			let investments = this.internalInvestmentsSelected
			if(this.internalInvestmentsSelected && this.internalInvestmentsSelected.constructor.name != 'Array') {
				investments = [this.internalInvestmentsSelected]
			}
			this.$emit('update:investmentsSelected', investments)
			this.$emit('investmentsChanged', investments)
		}
	}
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>
<style lang="scss">
@import '../../styles/custom-variables';
.multiselect__select::before {
	border-color: $black transparent transparent transparent;
}
.multiselect__tags {
	border-radius: 0px;
	.multiselect__placeholder {
		color: $black
	}
	.multiselect__tag {
		background: #E2EBF5;
		color: $graphite;
		.multiselect__tag-icon::after {
			color: $darkblue;
		}
		.multiselect__tag-icon:hover::after,
		.multiselect__tag-icon:focus::after {
		color:  $darkblue; /* Replace with hover/focus color of 'x' */
		}
	}
}
.multiselect, .multiselect__input, .multiselect__single {
	font-size: 14px;
}
.multiselect__option--highlight {
  background-color: #E2EBF5;
  background: #E2EBF5;
  color: $graphite; 
}

.multiselect__option--selected {
	background-color: white;
	position: relative;
	span {
		text-shadow: .05px 0 currentColor, -.05px 0 currentColor, 0 .05px currentColor, 0 -.05px currentColor;
	}
}

.multiselect__option--selected::after {
  content: 'Remove';
  color:  $darkblue;
  position: absolute;
  right: 10px;
  font-weight: normal;
}

.multiselect__option[data-select]::after {
  content: none;
}

.multiselect__option--selected[data-deselect]::after {
  content: 'Remove' !important;
  color:  $darkblue;
  position: absolute;
  right: 10px;
  font-weight: normal;
  background: white;
}

.multiselect__option--selected.multiselect__option--highlight {
	background: #E2EBF5;
	color: $graphite;
}
.multiselect__option--selected.multiselect__option--highlight::after {
  background-color: #E2EBF5;
  color: $darkblue;
}

.multiselect__option {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
  text-align: left;
}

.multiselect__content {
  max-width: 100%;
  overflow: hidden;
}


</style>
